import { Page, View, Text, Link } from '@react-pdf/renderer';
import { styles } from './styles';
import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import { SafeImage } from '../../components/SafeImage';
import { useTranslation } from 'react-i18next';
import { globalStyles } from '../../styles';
import { getImageSrc, joinLastWithAmpersand } from '@utils/fn';
import { useMemo } from 'react';

import { truncateText, normalizeText } from '../../../utils';
import { SPONSOR_EMAIL } from '@utils/constants';

export interface BoostYourRevenuePageProps {
  event: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  header: JSX.Element;
  footer: JSX.Element;
}

const BoostYourRevenuePage = ({
  event,
  coLocatedEvents,
  header,
  footer,
}: BoostYourRevenuePageProps) => {
  const { t, i18n } = useTranslation();

  const testimonialsItems = useMemo(
    () =>
      event?.website_content?.testimonials
        ?.filter(
          e =>
            e.is_partner &&
            e?.first_name &&
            e?.last_name &&
            e?.citation &&
            e?.company_name
        )
        ?.sort((a, b) => a.order - b.order)
        ?.map((e, id) => ({
          id: String(id),
          jobTitle: e?.job_title,
          companyName: e?.company_name,
          companyLogoLink: e?.company_logo,
          citation: e?.citation,
        }))
        ?.slice(0, 3) ?? [],
    [event?.website_content?.testimonials]
  );

  const hasMiddleItem = testimonialsItems?.length === 3;

  const sponsorSubject = t('sponsorMailto.subject', { eventName: event?.name });
  const sponsorBody = t('sponsorMailto.body', { eventName: event?.name });
  const sponsorMailHref = `mailto:${SPONSOR_EMAIL}?subject=${encodeURIComponent(sponsorSubject)}&body=${encodeURIComponent(sponsorBody)}`;

  if (!event?.colocated) {
    return null;
  }

  return (
    <Page size="A4" orientation="portrait" style={styles.page}>
      {header}

      <View style={styles.contentWrapper}>
        <Text
          style={{
            ...styles.title,
            color: event?.design?.themeColor,
          }}
        >
          {t('boost-your-revenue-pdf-title-1')}
        </Text>

        <View
          style={{
            ...globalStyles.lineBar,
            borderColor: event?.design?.themeColor ?? 'black',
            marginLeft: 0,
            marginBottom: i18n.language === 'en' ? 25 : 15,
            width: 89,
          }}
        />

        <Text
          style={{ ...styles.description, color: event?.design?.themeColor }}
        >
          {t('boost-your-revenue-pdf-title-2', {
            eventName: event?.name,
            numberOfColocatedEvents: coLocatedEvents?.length,
          })}
        </Text>

        <View style={styles.columnsWrapper}>
          <View style={styles.leftColumn}>
            <View style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[1, 2, 3, 4, 5, 6].map(item => {
                return (
                  <View key={`boost-youir-revenue-item-${item}`}>
                    <Text>
                      <Text
                        style={{
                          ...styles.itemTitle,
                          color: event?.design?.themeColor,
                        }}
                      >
                        {t(`boost-your-revenue-item-${item}-title`)}:{' '}
                      </Text>

                      <Text style={styles.itemText}>
                        {t(`boost-your-revenue-item-${item}-text`, {
                          numberOfColocatedEvents: coLocatedEvents?.length,
                          eventNames: joinLastWithAmpersand(
                            coLocatedEvents?.map(e => e.name) ?? []
                          ),
                        })}
                      </Text>
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>

          <View style={styles.rightColumn}>
            <View
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
              }}
            >
              <SafeImage
                style={styles.eventLogo}
                src={getImageSrc(event?.images?.logo ?? '')}
              />

              <View
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Text
                  style={{
                    ...styles.coLocatedWithTitle,
                    color: event?.design?.themeColor,
                  }}
                >
                  {t('co-located-with')}:
                </Text>

                {coLocatedEvents
                  ?.filter(e => e.id !== event.id)
                  ?.map(e => (
                    <SafeImage
                      key={`co-located-event-${e.id}`}
                      style={styles.image}
                      src={getImageSrc(e.images?.logo ?? '')}
                    />
                  ))}

                <View style={styles.buttonWrapper}>
                  <Link href={sponsorMailHref} style={styles.linkText}>
                    {t('apply-as-partner')}
                  </Link>
                </View>
              </View>
            </View>
          </View>
        </View>

        <View style={{ marginTop: i18n.language === 'en' ? 20 : 10 }}>
          <Text
            style={{
              ...styles.title,
              color: event?.design?.themeColor,
            }}
          >
            {t('what-our-partners-say')}
          </Text>

          <View
            style={{
              ...globalStyles.lineBar,
              borderColor: event?.design?.themeColor ?? 'black',
              marginLeft: 0,
              marginBottom: i18n.language === 'en' ? 15 : 5,
              width: 89,
            }}
          />

          <View
            style={{
              ...styles.testimonialsWrapper,
              justifyContent: hasMiddleItem ? 'space-between' : 'flex-start',
              gap: hasMiddleItem ? undefined : 10,
            }}
          >
            {testimonialsItems?.map((item, index) => {
              const isMiddleItem =
                (index === 1 && hasMiddleItem) || testimonialsItems?.length < 3;

              return (
                <View
                  key={`testimonial-item-${item.id}`}
                  style={{
                    ...styles.testimonialItemWrapper,
                    height: isMiddleItem ? 190 : 160,
                  }}
                >
                  <View
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Text
                      style={{
                        ...styles.testimonialCitation,
                      }}
                    >
                      {'"'}
                      {truncateText(normalizeText(item?.citation), 250)}
                      {'"'}
                    </Text>
                  </View>

                  <SafeImage
                    style={styles.testimonialCompanyLogo}
                    src={getImageSrc(item?.companyLogoLink ?? '')}
                  />

                  <View
                    style={{
                      ...styles.testimonialFooterWrapper,
                      borderColor: event?.design?.themeColor ?? 'black',
                    }}
                  >
                    <Text style={styles.testimonialJobTitleText}>
                      {truncateText(normalizeText(item?.jobTitle), 60)}
                    </Text>
                    <Text style={styles.testimonialCompanyNameText}>
                      {truncateText(normalizeText(item?.companyName), 80)}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      </View>

      {footer}
    </Page>
  );
};

export default BoostYourRevenuePage;
